import { Button, Card, Heading, IconButton, Input, Text, toast } from "@overmap-ai/blocks"
import {
	type AssetType,
	OrganizationAccessLevel,
	type Payload,
	ABBREVIATION_MAX_LENGTH,
	ABBREVIATION_MIN_LENGTH,
	ERROR_MSG_UNEXPECTED,
} from "@overmap-ai/common"
import { APIError, selectAssetTypeById } from "@overmap-ai/core"
import { getRouteApi, useNavigate } from "@tanstack/react-router"
import { type FocusEventHandler, memo, useCallback } from "react"

import { ViewContainer } from "../../components/Containers/ViewContainer"
import { LuDynamicIcon } from "../../components/Icons/LuDynamicIcon"
import type { IconType } from "../../components/Icons/typings"
import { OrganizationAccessRequired } from "../../components/OrganizationAccessRequired/AccessRequired"
import { IconColorPickerPopover } from "../../components/Popovers/IconColorPickerPopover"
import { RouteLinksCard } from "../../components/RouteLinksCard/RouteLinksCard"
import type { RouteLinksCardRoute } from "../../components/RouteLinksCard/typings"
import { TITLE_MAX_LENGTH } from "../../constants"
import { useTextConfirmationDialog } from "../../contexts/hooks"
import { useUserOrganizationAccess } from "../../hooks/users"
import { useBlurInput } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { useAppSelector } from "../../store"

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/settings/asset-types/$assetTypeId")

const routes: RouteLinksCardRoute[] = [
	{
		icon: modelIconMapping.settings,
		label: "General",
		description: "Description and broader settings",
		from: "/$organizationId/settings/asset-types/$assetTypeId",
		to: "/$organizationId/settings/asset-types/$assetTypeId/general",
	},
	{
		icon: modelIconMapping.status,
		label: "Statuses",
		description: "Manage asset type statuses",
		from: "/$organizationId/settings/asset-types/$assetTypeId",
		to: "/$organizationId/settings/asset-types/$assetTypeId/statuses",
	},
	{
		icon: modelIconMapping.identifier,
		label: "Identifiers",
		description: "Manage identifiers such as QR codes and barcodes",
		from: "/$organizationId/settings/asset-types/$assetTypeId",
		to: "/$organizationId/settings/asset-types/$assetTypeId/identifiers",
	},
	{
		icon: modelIconMapping.form,
		label: "Fields",
		description: "Manage asset type fields",
		from: "/$organizationId/settings/asset-types/$assetTypeId",
		to: "/$organizationId/settings/asset-types/$assetTypeId/fields",
	},
	{
		icon: modelIconMapping.attachment,
		label: "Attachments",
		description: "Manage asset type attachments",
		from: "/$organizationId/settings/asset-types/$assetTypeId",
		to: "/$organizationId/settings/asset-types/$assetTypeId/attachments",
	},
]

export const AssetTypeSettingsDetailView = memo(() => {
	const { assetTypeId } = routeApi.useParams()
	const assetType = useAppSelector(selectAssetTypeById(assetTypeId))

	if (!assetType) {
		throw new Error(`expected asset type to be defined, got ${assetType}`)
	}

	const navigate = useNavigate()
	const openTextConfirmationDialog = useTextConfirmationDialog()
	const isOrganizationAdmin = useUserOrganizationAccess(OrganizationAccessLevel.ADMIN)

	const handlePatchAssetType = useCallback(
		(assetTypePayload: Partial<Payload<AssetType>>) => {
			void sdk.assetTypes.update({ uuid: assetType.uuid, ...assetTypePayload })
		},
		[assetType.uuid],
	)

	const handleNameInputBlur = useCallback<FocusEventHandler<HTMLInputElement>>(
		(e) => {
			const value = e.target.value
			handlePatchAssetType({ name: value })
		},
		[handlePatchAssetType],
	)

	const nameInputProps = useBlurInput<HTMLInputElement>(assetType.name, handleNameInputBlur, true)

	const handleAbbrInputBlur = useCallback<FocusEventHandler<HTMLInputElement>>(
		(e) => {
			const value = e.target.value
			handlePatchAssetType({ abbr: value })
		},
		[handlePatchAssetType],
	)

	const abbrInputProps = useBlurInput<HTMLInputElement>(assetType.abbr, handleAbbrInputBlur, true)

	const handleOrderInputBlur = useCallback<FocusEventHandler<HTMLInputElement>>(
		(e) => {
			const value = e.target.valueAsNumber
			if (isNaN(value)) return
			handlePatchAssetType({ order: value })
		},
		[handlePatchAssetType],
	)

	const orderInputProps = useBlurInput<HTMLInputElement>(assetType.order.toString(), handleOrderInputBlur, true)

	const handleColorChange = useCallback(
		(color: string | null) => {
			if (!color) return
			handlePatchAssetType({ color: color })
		},
		[handlePatchAssetType],
	)

	const handleIconChange = useCallback(
		(icon: string | null) => {
			if (!icon) return
			handlePatchAssetType({ icon })
		},
		[handlePatchAssetType],
	)

	const handleDelete = useCallback(() => {
		openTextConfirmationDialog((close) => ({
			title: `Delete ${assetType.name}`,
			description: "Are you sure you want to delete this asset type?",
			confirmString: "delete",
			confirmMessage: "Type 'delete' to confirm",
			actionLabel: "Delete",
			onClose: close,
			onConfirm: async () => {
				await navigate({
					from: "/$organizationId/settings/asset-types/$assetTypeId",
					to: "/$organizationId/settings/asset-types",
				})

				sdk.assetTypes.delete(assetType.uuid).catch((error) => {
					toast.error({
						title: "Failed to delete asset type",
						description: error instanceof APIError ? error.message : ERROR_MSG_UNEXPECTED,
					})
				})
			},
		}))
	}, [assetType.name, assetType.uuid, navigate, openTextConfirmationDialog])

	return (
		<ViewContainer maxWidth="2xl">
			<section className="flex h-max w-full flex-col gap-4">
				<div className="flex w-full flex-col gap-1">
					<Heading size="xl">{assetType.name}</Heading>
					<Text size="sm" accentColor="base">
						Manage asset type settings and fields.
					</Text>
				</div>

				<Card className="flex w-full flex-col gap-4" size="sm">
					<div className="flex w-full flex-col gap-4 sm:flex-row sm:gap-2">
						<div className="flex w-full flex-col gap-1">
							<Text accentColor="base" size="sm">
								Icon & name
							</Text>
							<div className="flex w-full gap-2">
								<IconColorPickerPopover
									className="w-[340px]"
									color={assetType.color}
									onColorChange={handleColorChange}
									selectedIcon={assetType.icon}
									onIconChange={handleIconChange}
									size="sm"
								>
									<IconButton
										aria-label="asset type icon"
										variant="soft"
										size="md"
										accentColor="base"
										disabled={!isOrganizationAdmin}
									>
										<LuDynamicIcon icon={assetType.icon as IconType} color={assetType.color} />
									</IconButton>
								</IconColorPickerPopover>

								<Input.Root className="grow" variant="outline">
									<Input.Field
										placeholder="Enter a name"
										maxLength={TITLE_MAX_LENGTH}
										disabled={!isOrganizationAdmin}
										{...nameInputProps}
									/>
								</Input.Root>
							</div>
						</div>
						<div className="flex w-full flex-col gap-1">
							<Text accentColor="base" size="sm">
								Abbreviation
							</Text>

							<Input.Root className="grow" variant="outline">
								<Input.Field
									minLength={ABBREVIATION_MIN_LENGTH}
									max={ABBREVIATION_MAX_LENGTH}
									placeholder="Enter an abbreviation"
									disabled={!isOrganizationAdmin}
									{...abbrInputProps}
								/>
							</Input.Root>
						</div>
					</div>

					<div className="flex w-full flex-col gap-1">
						<Text accentColor="base" size="sm">
							Order
						</Text>

						<Input.Root variant="outline">
							<Input.Field
								type="number"
								placeholder="Enter a number"
								disabled={!isOrganizationAdmin}
								{...orderInputProps}
							/>
						</Input.Root>
					</div>
				</Card>

				<RouteLinksCard routes={routes} size="sm" />

				<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.ADMIN} type="hide">
					<Button className="w-max" size="sm" accentColor="danger" onClick={handleDelete}>
						Delete
					</Button>
				</OrganizationAccessRequired>
			</section>
		</ViewContainer>
	)
})
AssetTypeSettingsDetailView.displayName = "AssetTypeSettingsDetailView"
